import { useState } from 'react'
import { Botao } from '../../components/Botao'
import { Cartao, ListaDeDados } from '../../components/ListaDeDados'
import { TEXTOS } from '../../lib/glossario'
import { EXPLICACAO_DO_PAPEL } from '../../lib/papeis'
import type { Papel } from '../../types/usuario'
import type { UsuarioLogado } from '../../types/gmud'
import { EdicaoDosDadosPessoais } from './EdicaoDosDadosPessoais'

/**
 * Quem a pessoa é, segundo o cadastro — e o que isso significa na prática.
 *
 * **O lado do contrato aparece por extenso, não como sigla.** "CONTRATADA" não explica nada a
 * quem acabou de tomar um "esta tela não é do seu perfil"; a frase que explica é "as telas de
 * fiscalização são da equipe da Prefeitura, que mede a execução". Sem isso, a recusa parece
 * arbitrária e vira chamado no suporte.
 *
 * Os papéis vêm com a mesma explicação que o administrador leu ao concedê-los, do mesmo arquivo:
 * os dois discutindo acesso com definições diferentes é como se descobre que alguém escreveu a
 * explicação duas vezes.
 *
 * Nome e e-mail se corrigem aqui mesmo, no lugar onde aparecem (Teste de uso 1). A organização
 * não: ela decide o lado do contrato, e mudá-la é ato do administrador.
 *
 * @param usuario o usuário logado
 * @returns o cartão com os dados da conta
 */
export function DadosDaConta({ usuario }: { usuario: UsuarioLogado }) {
  const [editando, setEditando] = useState(false)
  const [salvou, setSalvou] = useState(false)
  const lado = usuario.papelContratual
  const explicacaoDoLado = lado ? TEXTOS.perfil[lado] : TEXTOS.perfil.semLado

  const papeis = usuario.papeis.filter((papel): papel is Papel => papel in EXPLICACAO_DO_PAPEL)

  return (
    <div className="space-y-4">
      <Cartao titulo={TEXTOS.perfil.dados}>
        {editando ? (
          <EdicaoDosDadosPessoais
            usuario={usuario}
            aoTerminar={(salvo) => {
              setEditando(false)
              setSalvou(salvo)
            }}
          />
        ) : (
          <>
            <ListaDeDados
              dados={[
                { rotulo: TEXTOS.perfil.corrigir.nome, valor: usuario.nome },
                { rotulo: TEXTOS.perfil.corrigir.email, valor: usuario.email },
                { rotulo: 'Organização', valor: usuario.organizacao?.nome },
              ]}
            />
            {salvou && (
              <p role="status" className="mt-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                {TEXTOS.perfil.corrigir.salvos}
              </p>
            )}
            <Botao
              className="mt-4"
              onClick={() => {
                setEditando(true)
                setSalvou(false)
              }}
            >
              {TEXTOS.perfil.corrigir.abrir}
            </Botao>
          </>
        )}
        <div className="mt-5 rounded-lg bg-slate-50 p-4">
          <p className="text-xs font-medium text-slate-500">
            {TEXTOS.perfil.ladoDoContrato}
          </p>
          <p className="mt-1 max-w-prose text-sm text-slate-700">{explicacaoDoLado}</p>
        </div>
      </Cartao>

      <Cartao titulo={TEXTOS.perfil.meusPapeis}>
        {papeis.length === 0 ? (
          <p className="max-w-prose text-sm text-slate-600">{TEXTOS.perfil.semPapel}</p>
        ) : (
          <ul className="space-y-3">
            {papeis.map((papel) => (
              <li key={papel}>
                <p className="text-sm font-semibold text-slate-900">{EXPLICACAO_DO_PAPEL[papel].rotulo}</p>
                <p className="max-w-prose text-sm text-slate-600">{EXPLICACAO_DO_PAPEL[papel].ajuda}</p>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </div>
  )
}
